import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Inbox, Loader2 } from "lucide-react";
import { fileUrl, fmtL } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

export function Logo({ size = "md", testid = "opl-logo" }) {
  const big = size === "lg";
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <img src="/logo-white.png" alt="Oneiros The Sports Club" className={`logo-w ${big ? "h-20" : "h-14"} w-auto object-contain`} />
      <img src="/logo-navy.png" alt="Oneiros The Sports Club" className={`logo-n ${big ? "h-20" : "h-14"} w-auto object-contain`} />
      <div className={`leading-tight border-l border-current/20 pl-3 ${big ? "border-white/20" : "border-slate-400/30"}`}>
        <div className={`font-display font-extrabold ${big ? "text-2xl" : "text-base"} tracking-tight`}>OPL <span className="gold-text">Season 6</span></div>
      </div>
    </div>
  );
}

const STATUS_STYLE = {
  IDLE: "bg-slate-800/60 text-slate-300 border-slate-600",
  READY: "bg-[#132742] text-blue-300 border-blue-500",
  LIVE: "bg-red-500/15 text-red-400 border-red-500",
  GOING_ONCE: "bg-amber-500/15 text-amber-300 border-amber-500",
  GOING_TWICE: "bg-rose-500/20 text-rose-300 border-rose-500",
  CLOSED: "bg-orange-500/15 text-orange-300 border-orange-500",
  SOLD: "bg-emerald-500/20 text-emerald-300 border-emerald-500",
  UNSOLD: "bg-gray-500/20 text-gray-300 border-gray-500",
  PAUSED: "bg-yellow-500/15 text-yellow-300 border-yellow-500",
  SESSION_COMPLETE: "bg-indigo-500/20 text-indigo-300 border-indigo-500",
};

export function StatusPill({ status, big, testid = "auction-status-pill" }) {
  const label = status === "IDLE" ? "NOT STARTED" : status === "CLOSED" ? "BIDDING CLOSED" : (status || "").replace("_", " ");
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-2 border rounded-full font-mono font-bold tracking-widest ${big ? "px-5 py-2 text-lg" : "px-3 py-1 text-[11px]"} ${STATUS_STYLE[status] || STATUS_STYLE.IDLE}`}>
      {["LIVE", "GOING_ONCE", "GOING_TWICE"].includes(status) && <span className="h-2 w-2 rounded-full bg-current live-dot" />}
      {label}
    </span>
  );
}

export const AUCTION_STATUS_BADGE = {
  upcoming: "text-blue-300 border-blue-500/40", live: "text-red-400 border-red-500/50", sold: "text-emerald-300 border-emerald-500/40",
  unsold: "text-slate-300 border-slate-500/40", withdrawn: "text-orange-300 border-orange-500/40",
};

export function Badge({ children, className = "", testid }) {
  return <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ${className}`}>{children}</span>;
}

export function PlayerAvatar({ player, size = 48, className = "" }) {
  const initials = (player?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  if (player?.photo) return <img src={fileUrl(player.photo)} alt={player.name} style={{ width: size, height: size }} className={`rounded-lg object-cover ${className}`} />;
  return (
    <div style={{ width: size, height: size, fontSize: size / 2.8 }} className={`rounded-lg bg-gradient-to-br from-[#172744] to-[#0B1323] border border-[#E5B842]/20 grid place-items-center font-display font-black text-[#E5B842]/80 ${className}`}>
      {initials}
    </div>
  );
}

export function TeamBadge({ team, size = 36 }) {
  if (team?.logo) return <img src={fileUrl(team.logo)} alt={team.name} style={{ width: size, height: size }} className="rounded-md object-cover" />;
  return (
    <div style={{ width: size, height: size, background: `${team?.color || "#E5B842"}22`, borderColor: team?.color || "#E5B842", color: team?.color || "#E5B842", fontSize: size / 3 }}
      className="rounded-md border grid place-items-center font-display font-black shrink-0">
      {team?.short_name || (team?.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

export const Money = ({ v, className = "", testid }) => <span data-testid={testid} className={`num ${className}`}>{fmtL(v)}</span>;

export function EmptyState({ title = "Nothing here yet", hint, testid = "empty-state" }) {
  return (
    <div data-testid={testid} className="surface rounded-xl p-10 text-center text-slate-400">
      <Inbox className="mx-auto h-8 w-8 text-[#E5B842]/60" />
      <p className="mt-3 font-semibold text-slate-200">{title}</p>
      {hint && <p className="text-sm mt-1">{hint}</p>}
    </div>
  );
}

export const Spinner = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 text-slate-400 p-8" data-testid="loading"><Loader2 className="h-4 w-4 animate-spin" /> {label}</div>
);

export function PageHeader({ title, crumbs = [], children, eyebrow }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        <nav className="flex items-center gap-1 text-xs text-slate-500 mb-2" data-testid="breadcrumbs">
          <Link to="/admin" className="hover:text-[#E5B842]">Admin</Link>
          {crumbs.map((c) => (<span key={c} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" />{c}</span>))}
        </nav>
        {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="page-title">{title}</h1>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function Stat({ label, value, sub, testid, accent }) {
  return (
    <div className="surface rounded-xl p-5 hover:border-[#E5B842]/30 transition-colors" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-2 num text-2xl font-bold ${accent ? "gold-text" : "text-white"}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function ConfirmDialog({ open, onOpenChange, title, description, onConfirm, confirmLabel = "Confirm", requireReason, destructive, testid = "confirm-dialog" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onConfirm(reason); onOpenChange(false); setReason(""); } finally { setBusy(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-[#0B1323] border-[#E5B842]/20" data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        {requireReason && (
          <Textarea data-testid="confirm-reason-input" placeholder="Reason (required, will be logged)" value={reason} onChange={(e) => setReason(e.target.value)} />
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok-btn" disabled={busy || (requireReason && reason.trim().length < 3)} onClick={go}
            className={destructive ? "bg-red-600 hover:bg-red-700 text-white" : ""}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function useConfirm() {
  const [cfg, setCfg] = useState(null);
  const dialog = cfg ? <ConfirmDialog open onOpenChange={(o) => !o && setCfg(null)} {...cfg} /> : null;
  return [dialog, setCfg];
}
